import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Building2, Check, KeyRound, Loader2, Network, PlugZap } from "lucide-react";

import { loadOpenRouterKeyInfo, setOpenRouterKey } from "@/server/ai-key.functions";
import type { OpenRouterKeyInfo } from "@/server/ai-key.functions";
import { checkAI } from "@/braining/ai-check";
import { loadProxyInfo, setProxy } from "@/braining/ai-proxy.functions";
import type { ProxyInfo } from "@/braining/ai-proxy.functions";
import type { AiCheckResult } from "@/braining/ai-check";
import { loadDadataInfo, setDadataKey, type DadataInfo } from "@/braining/dadata.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/settings")({ component: SettingsPage });

/**
 * Ключ AI-провайдера.
 *
 * Без него разбор материалов и ассистент не работают вовсе: система умеет
 * прочитать файл и достать из него текст, но смысла из текста не извлечёт.
 *
 * Ключ хранится в базе (`ai_settings`), а не в переменных окружения: иначе
 * поменять его можно было бы только выкаткой. В браузер он не возвращается
 * никогда — экран показывает лишь хвост, чтобы человек убедился, что ввёл
 * тот самый.
 */
function SettingsPage() {
  const { user } = Route.useRouteContext();
  const isAdmin = user?.role === "admin";
  const [info, setInfo] = useState<OpenRouterKeyInfo | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<AiCheckResult | null>(null);
  const [proxy, setProxyInfo] = useState<ProxyInfo | null>(null);
  const [proxyDraft, setProxyDraft] = useState("");
  const [savingProxy, setSavingProxy] = useState(false);

  useEffect(() => {
    void loadOpenRouterKeyInfo()
      .then(setInfo)
      .catch((e: unknown) => {
        console.error("[ai-key] не удалось прочитать состояние ключа", e);
        toast.error("Не удалось узнать, задан ли ключ");
      });
    void loadProxyInfo()
      .then(setProxyInfo)
      .catch((e: unknown) => {
        console.error("[ai-proxy] не удалось прочитать состояние прокси", e);
      });
  }, []);

  /**
   * Сохранение прокси.
   *
   * Пустая строка убирает его: это не ошибка ввода, а решение ходить к
   * провайдеру напрямую. Проверку связи после сохранения не запускаем сами —
   * она рядом, и нажать её человек решает сам.
   */
  async function saveProxy(url: string) {
    setSavingProxy(true);
    try {
      await setProxy(url);
      setProxyDraft("");
      setProxyInfo(await loadProxyInfo());
      setCheck(null);
      toast.success(url.trim() ? "Прокси сохранён" : "Прокси убран — идём напрямую");
    } catch (e) {
      // Сервер объясняет причину словами: не тот формат, неизвестный
      // протокол. Пересказывать нечего.
      console.error("[ai-proxy] не удалось сохранить прокси", e);
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить прокси");
    } finally {
      setSavingProxy(false);
    }
  }

  async function save() {
    const key = draft.trim();
    if (key.length < 8) return;

    setSaving(true);
    try {
      await setOpenRouterKey(key);
      setDraft("");
      setInfo(await loadOpenRouterKeyInfo());
      toast.success("Ключ сохранён");
    } catch (e) {
      console.error("[ai-key] не удалось сохранить ключ", e);
      toast.error("Не удалось сохранить ключ");
    } finally {
      setSaving(false);
    }
  }

  /**
   * Проверка связи одним коротким запросом.
   *
   * Нужна потому, что отказ провайдера на экране сметы выглядит одинаково для
   * двух разных бед: ключ не тот или до провайдера не пускают по стране.
   * Здесь видно, что именно, — и видно самому владельцу инстанса, без чтения
   * логов сервера.
   */
  async function runCheck() {
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await checkAI());
    } catch (e) {
      console.error("[ai-key] проверка связи не выполнилась", e);
      toast.error("Не удалось выполнить проверку");
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Ключи</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Подключения к внешним сервисам. Правит администратор.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium">Ключ OpenRouter</span>
            <KeyState info={info} />
          </div>

          <p className="text-sm text-muted-foreground">
            Нужен для разбора материалов и ассистента. Оплата идёт по ключу, который вы сюда
            впишете, — свой можно поменять или убрать в любой момент на{" "}
            <span className="whitespace-nowrap">openrouter.ai</span>.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="password"
              value={draft}
              placeholder="sk-or-…"
              autoComplete="off"
              className="w-80 font-mono"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
              }}
            />
            <Button onClick={() => void save()} disabled={draft.trim().length < 8 || saving}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {info?.isSet ? "Заменить" : "Сохранить"}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Ключ уходит на сервер и ложится в базу. Обратно в браузер он не возвращается — ни здесь,
            ни где-либо ещё видно его не будет, только последние четыре знака.
          </p>

          {/*
            Прокси стоит здесь же, а не отдельным экраном: без него ключ
            бесполезен. OpenRouter не пускает с российских адресов и отвечает
            403 — на экране сметы это выглядело как проблема с ключом, и
            причину человек искал не там.
          */}
          <div className="space-y-2 border-t pt-4">
            <div className="flex items-center gap-2">
              <Network className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">Прокси к провайдеру</span>
              <ProxyState info={proxy} />
            </div>

            <p className="text-sm text-muted-foreground">
              OpenRouter не отвечает на запросы с российских адресов. Сервер ходит к нему через
              прокси — вставьте строку так, как её выдал продавец:{" "}
              <span className="whitespace-nowrap font-mono text-xs">хост:порт:логин:пароль</span>.
              Ссылка вида{" "}
              <span className="whitespace-nowrap font-mono text-xs">
                http://логин:пароль@хост:порт
              </span>{" "}
              тоже подойдёт.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="password"
                value={proxyDraft}
                placeholder="185.12.34.56:8000:user123:pass456"
                autoComplete="off"
                className="w-96 font-mono"
                onChange={(e) => setProxyDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveProxy(proxyDraft);
                }}
              />
              <Button
                variant="outline"
                onClick={() => void saveProxy(proxyDraft)}
                disabled={proxyDraft.trim().length < 8 || savingProxy}
              >
                {savingProxy && <Loader2 className="size-4 animate-spin" />}
                {proxy?.isSet ? "Заменить" : "Сохранить"}
              </Button>
              {/* Убрать прокси — обычное решение, а не поломка: сервер может
                  переехать туда, откуда провайдер пускает напрямую. */}
              {proxy?.isSet && proxy.source === "db" && (
                <Button variant="ghost" onClick={() => void saveProxy("")} disabled={savingProxy}>
                  Убрать
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t pt-4">
            <Button variant="outline" onClick={() => void runCheck()} disabled={checking}>
              {checking ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <PlugZap className="size-4" />
              )}
              Проверить связь
            </Button>
            <span className="text-xs text-muted-foreground">
              Один короткий запрос к провайдеру — видно, дело в ключе или в доступе с сервера.
            </span>
          </div>

          {check && (
            <div
              className={cn(
                "rounded-md border px-3 py-2.5 text-sm",
                check.ok
                  ? "border-success/30 bg-success/5"
                  : "border-destructive/30 bg-destructive/5",
              )}
            >
              <div className="font-medium">{check.message}</div>
              {/* Ответ провайдера как есть. Пересказывать его своими словами
                  нельзя: именно в нём написано, что на самом деле случилось. */}
              {check.detail && (
                <pre className="mt-1.5 overflow-x-auto whitespace-pre-wrap break-all text-xs text-muted-foreground">
                  {check.detail}
                </pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && <DadataCard />}
    </div>
  );
}

/**
 * Ключ DaData — сверка подрядчиков (ИП и ООО) с ЕГРИП/ЕГРЮЛ.
 *
 * Бесплатный тариф: 10 000 запросов в сутки, поиск по ИНН в него входит.
 * Нужен «API-ключ» из профиля; «Секретный ключ» — для платной
 * стандартизации, он здесь не подойдёт, и сервер скажет об этом сразу:
 * перед сохранением ключ пробуется одним запросом.
 */
function DadataCard() {
  const [info, setInfo] = useState<DadataInfo | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadDadataInfo()
      .then(setInfo)
      .catch((e: unknown) => console.error("[dadata] не удалось прочитать состояние ключа", e));
  }, []);

  async function save(key: string) {
    setBusy(true);
    try {
      const res = await setDadataKey({ data: { key } });
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      setDraft("");
      setInfo(await loadDadataInfo());
      toast.success(res.probe ? `Ключ работает — нашёлся «${res.probe}»` : "Ключ DaData убран");
    } catch (e) {
      console.error("[dadata] не удалось сохранить ключ", e);
      toast.error("Не удалось сохранить ключ");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div className="flex items-center gap-2">
          <Building2 className="size-4 text-muted-foreground" />
          <span className="text-sm font-medium">Ключ DaData</span>
          {!info ? (
            <span className="text-xs text-muted-foreground">проверяем…</span>
          ) : info.isSet ? (
            <span className="flex items-center gap-1 text-xs text-success-ink">
              <Check className="size-3.5" />
              <span className="font-mono">{info.masked}</span>
            </span>
          ) : (
            <span className="text-xs text-warning-ink">
              не задан — подрядчики не сверяются с реестром
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Сверка ИП и ООО с ЕГРИП/ЕГРЮЛ: название, ОГРН, КПП, действует ли организация, кто
          руководитель. Нужен «API-ключ» из профиля на dadata.ru — бесплатного тарифа хватает.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="password"
            value={draft}
            placeholder="API-ключ"
            autoComplete="off"
            className="w-80 font-mono"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save(draft);
            }}
          />
          <Button onClick={() => void save(draft)} disabled={draft.trim().length < 20 || busy}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {info?.isSet ? "Заменить" : "Сохранить"}
          </Button>
          {info?.isSet && (
            <Button variant="ghost" onClick={() => void save("")} disabled={busy}>
              Убрать
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Состояние прокси.
 *
 * Как и у ключа, важен источник: адрес из переменной окружения с экрана не
 * поменять, и человек, который думает, что поменял, ошибётся молча.
 */
function ProxyState({ info }: { info: ProxyInfo | null }) {
  if (!info) return <span className="text-xs text-muted-foreground">проверяем…</span>;

  if (!info.isSet) {
    return <span className="text-xs text-warning-ink">не задан — провайдер отвечает 403</span>;
  }

  return (
    <span className="flex items-center gap-1 text-xs text-success-ink">
      <Check className="size-3.5" />
      <span className="font-mono">{info.masked}</span>
      {info.source === "env" && (
        <span className="text-muted-foreground"> · из окружения, с экрана не меняется</span>
      )}
    </span>
  );
}

/**
 * Состояние ключа словами, а не значком.
 *
 * Источник важен: ключ из переменной окружения не поменять с экрана, и если
 * человек будет думать, что поменял, — он ошибётся молча.
 */
function KeyState({ info }: { info: OpenRouterKeyInfo | null }) {
  if (!info) return <span className="text-xs text-muted-foreground">проверяем…</span>;

  if (!info.isSet) {
    return <span className="text-xs text-warning-ink">не задан — разбор не работает</span>;
  }

  return (
    <span className="flex items-center gap-1 text-xs text-success-ink">
      <Check className="size-3.5" />
      {info.masked}
      {info.source === "env" && (
        <span className="text-muted-foreground"> · из окружения, с экрана не меняется</span>
      )}
    </span>
  );
}
